import { of, EMPTY, from, Observable } from 'rxjs';
import { mergeMap, map, catchError, bufferCount, take, filter } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import get from 'lodash.get';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import Global from '../../../store/global-selectors';
import { emptyArray } from 'ui-components/default-props/empty';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { BulkErrorsAndWarningsService } from '../../../services/bulk-errors-and-warnings-service';
import * as ShipmentsActions from '../../../store/shipments/shipments.actions';
import * as PrintActions from '../../../store/printing/print.actions';
import * as SendNotificationsActions from '../../../store/send-notifications/send-notifications.actions';
import * as DocumentActions from '../../../store/documents/documents.actions';
import * as BatchDetailActions from '../../../store/batches/details/batch.details.actions';
import * as FilteringActions from '../../../store/shipments/filtering/shipments.filtering.actions';
import { DocumentType } from '../../../enums/document-type';
import { DocumentService } from '../../../services/document-service';
import * as Actions from './shipments.grid.actions';
import * as GridHeaderActions from './shipments.grid.header.actions';
import * as ShipmentDetailsApi from '../../../store/shipments/shipment-details/shipments.details.api';
import { BATCHES_VIEW_ID } from '../../../components/shipping-grid/views/view-id-constants';
import { bulkErrorsAndWarningsModalObservable } from '../../../store/bulk-errors-and-warnings/bulk-errors-and-warnings.observables';
import * as ShipmentsApi from '../shipments.api';
import { amazonShipNotifyFeedStatusMap } from '../../../constants/marketplace-notifications';
import { messages } from '../messages';
import { getSelectedViewFromPathName } from '../../../pages/shipping-page/shipping-left-nav/shipping-left-nav-context';
import { PusherService } from '../../../services/pusher-service';
import { FeatureFlags } from '../../../feature-flags/feature-flags';

export const sendShipmentNotification = (action$, { getState }) =>
  action$.pipe(
    ofType(GridHeaderActions.SHIPMENTS_GRID_SEND_SHIPMENT_NOTIFICATION),
    mergeMap((action) => {
      const sendingShipmentNotificationProps = {
        title: <FormattedMessage {...messages.sendingShimentNotificationTitle} />,
        content: <FormattedMessage {...messages.sendingShimentNotificationContent} />,
        notificationType: 'info',
        duration: 1000,
      };
      NotificationService.showNotification({
        ...sendingShipmentNotificationProps,
      });

      const state = getState();
      const featureFlags = new FeatureFlags(state);
      if (featureFlags.enableAsyncSendNotifications) {
        return ShipmentDetailsApi.sendNotificationsAsync(action.fulfillmentIds).pipe(
          mergeMap(() => {
            // TODO add pusher message handling. Currently it doesn't react to the response so we are ignoring it for now.
            return EMPTY;
          }),
        );
      }
      const fpids = Global.selectEntitiesFPIdsByFulfillmentIds(state, {
        fulfillmentIds: action.fulfillmentIds,
      });
      return of(SendNotificationsActions.retryNotifications(fpids));
    }),
  );

export const refreshGrid = (action$, { getState }) => {
  return action$.pipe(
    ofType(PrintActions.MARK_LABEL_PRINTED_SUCCESS, PrintActions.MARK_PACKING_SLIP_PRINTED_SUCCESS),
    mergeMap(() => {
      const state = getState();
      const selectedFulfillmentIds = Global.selectSelectedShipmentsSelectedShipmentIds(state);
      if (selectedFulfillmentIds.length === 0) {
        return EMPTY;
      }
      return of(ShipmentsActions.silentRequestGetShipments());
    }),
  );
};

export const changeOrderBy = (action$) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_GRID_CHANGE_ORDERBY),
    map(({ statusViewId }) => {
      if (statusViewId === BATCHES_VIEW_ID) {
        return BatchDetailActions.reloadBatchDetails();
      }

      return ShipmentsActions.requestGetShipments();
    }),
  );
};

export const downloadBulkPackingSlip = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.DOWNLOAD_SHIPMENT_GRID_PACKING_SLIPS),
    mergeMap(() => {
      const state = getState();
      const selectedFulfillmentIds = Global.selectSelectedShipmentsSelectedShipmentIds(state) || [];
      if (selectedFulfillmentIds.length === 0) {
        return EMPTY;
      }
      const fulfillmentPlanIds =
        Global.selectEntitiesFPIdsByFulfillmentIds(state, {
          fulfillmentIds: selectedFulfillmentIds,
        }) || emptyArray;
      const selectedFulfillmentPlans =
        Global.selectEntitiesFulfillmentPlansByIds(state, {
          fulfillmentPlanIds,
        }) || emptyArray;
      const someFulfillmentsHavePrintedPackingSlip = selectedFulfillmentPlans.some(
        (fulfillmentPlan) => fulfillmentPlan.isPackingSlipPrinted,
      );
      const shouldShowReprintWarning = Global.selectPrintSettingsShouldShowPackingSlipRePrintWarning(state);

      if (someFulfillmentsHavePrintedPackingSlip && shouldShowReprintWarning) {
        const shipmentsWithWarnings = selectedFulfillmentPlans.filter((fulfillmentPlan) => fulfillmentPlan.isPackingSlipPrinted);
        const mappedData = shipmentsWithWarnings.map(({ fulfillmentPlanId }) => ({
          id: fulfillmentPlanId,
          fulfillmentPlanId,
        }));
        const errorsAndWarnings = [BulkErrorsAndWarningsService.getPackingSlipPrintedWarning(mappedData)];

        const onContinueAction = () => {
          return DocumentActions.downloadDocument(DocumentType.PackingSlip, fulfillmentPlanIds);
        };
        const errorModal$ = bulkErrorsAndWarningsModalObservable({
          continueCanBeDisabled: true,
          dataType: 'fulfillmentPlanWithoutSalesOrder',
          errorsAndWarnings,
          fulfillmentPlanSetId: undefined,
          hasAdditionalAction: false,
          hasSelectedItems: !!selectedFulfillmentPlans.length,
          isBatchPrinting: false,
          onContinueAction,
          selectedItems: selectedFulfillmentPlans,
          title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
        });

        return errorModal$;
      }

      return of(DocumentActions.downloadDocument(DocumentType.PackingSlip, fulfillmentPlanIds));
    }),
  );
};

export const directPrintBulkPackingSlip = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.DIRECT_PRINT_SHIPMENT_GRID_PACKING_SLIPS),
    mergeMap(() => {
      const state = getState();
      const selectedFulfillmentIds = Global.selectSelectedShipmentsSelectedShipmentIds(state) || [];
      if (selectedFulfillmentIds.length === 0) {
        return EMPTY;
      }
      const fulfillmentPlanIds =
        Global.selectEntitiesFPIdsByFulfillmentIds(state, {
          fulfillmentIds: selectedFulfillmentIds,
        }) || emptyArray;
      const selectedFulfillmentPlans =
        Global.selectEntitiesFulfillmentPlansByIds(state, {
          fulfillmentPlanIds,
        }) || emptyArray;
      const someFulfillmentsHavePrintedPackingSlip = selectedFulfillmentPlans.some(
        (fulfillmentPlan) => fulfillmentPlan.isPackingSlipPrinted,
      );
      const shouldShowReprintWarning = Global.selectPrintSettingsShouldShowPackingSlipRePrintWarning(state);

      if (someFulfillmentsHavePrintedPackingSlip && shouldShowReprintWarning) {
        const shipmentsWithWarnings = selectedFulfillmentPlans.filter((fulfillmentPlan) => fulfillmentPlan.isPackingSlipPrinted);
        const mappedData = shipmentsWithWarnings.map(({ fulfillmentPlanId }) => ({
          id: fulfillmentPlanId,
          fulfillmentPlanId,
        }));
        const errorsAndWarnings = [BulkErrorsAndWarningsService.getPackingSlipPrintedWarning(mappedData)];

        const onContinueAction = () => {
          return DocumentActions.printDocument(
            DocumentType.PackingSlip,
            fulfillmentPlanIds,
            DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.PackingSlip, false),
          );
        };
        const errorModal$ = bulkErrorsAndWarningsModalObservable({
          continueCanBeDisabled: true,
          dataType: 'fulfillmentPlanWithoutSalesOrder',
          errorsAndWarnings,
          fulfillmentPlanSetId: undefined,
          hasAdditionalAction: false,
          hasSelectedItems: !!selectedFulfillmentPlans.length,
          isBatchPrinting: false,
          onContinueAction,
          selectedItems: selectedFulfillmentPlans,
          title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
          skipInventoryActions: true,
        });

        return errorModal$;
      }

      return of(
        DocumentActions.printDocument(
          DocumentType.PackingSlip,
          fulfillmentPlanIds,
          DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.PackingSlip, false),
        ),
      );
    }),
  );
};

export const printBulkPackingSlip = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_SHIPMENT_GRID_PACKING_SLIPS),
    mergeMap(({ autoPrint }) => {
      const state = getState();

      const selectedFulfillmentIds = Global.selectSelectedShipmentsSelectedShipmentIds(state) || [];
      if (selectedFulfillmentIds.length === 0) {
        return EMPTY;
      }

      const selectedFulfillmentPlanIds =
        Global.selectEntitiesFPIdsByFulfillmentIds(state, {
          fulfillmentIds: selectedFulfillmentIds,
        }) || emptyArray;
      const selectedFulfillmentPlans =
        Global.selectEntitiesFulfillmentPlansByIds(state, {
          fulfillmentPlanId: selectedFulfillmentPlanIds,
        }) || emptyArray;
      const someFulfillmentsHavePrintedPackingSlip = selectedFulfillmentPlans.some(
        (fulfillmentPlan) => fulfillmentPlan.isPackingSlipPrinted,
      );
      const shouldShowReprintWarning = Global.selectPrintSettingsShouldShowPackingSlipRePrintWarning(state);

      if (someFulfillmentsHavePrintedPackingSlip && shouldShowReprintWarning) {
        const fulfillmentsWithWarnings = selectedFulfillmentPlans.filter((fulfillmentPlan) => fulfillmentPlan.isPackingSlipPrinted);
        const mappedData = fulfillmentsWithWarnings.map(({ fulfillmentPlanId }) => ({
          id: fulfillmentPlanId,
          fulfillmentPlanId,
        }));
        const errorsAndWarnings = [BulkErrorsAndWarningsService.getPackingSlipPrintedWarning(mappedData)];

        const onContinueAction = (updatedData) => {
          const fulfillmentPlansToContinueWith = updatedData.selectedItems;
          const fulfillmentPlanIds = fulfillmentPlansToContinueWith.map((shipment) => shipment.fulfillmentPlanId);
          const someFulfillmentPlansHavePrintedPackingSlipAfterUserAct = fulfillmentPlansToContinueWith.some(
            (fulfillmentPlan) => fulfillmentPlan.isPackingSlipPrinted,
          );
          return PrintActions.printPackingSlip(fulfillmentPlanIds, someFulfillmentPlansHavePrintedPackingSlipAfterUserAct, true, autoPrint);
        };
        const errorModal$ = bulkErrorsAndWarningsModalObservable({
          continueCanBeDisabled: true,
          dataType: 'fulfillmentPlanWithoutSalesOrder',
          errorsAndWarnings,
          fulfillmentPlanSetId: undefined,
          hasAdditionalAction: false,
          hasSelectedItems: !!selectedFulfillmentPlans.length,
          isBatchPrinting: false,
          onContinueAction,
          selectedItems: selectedFulfillmentPlans,
          title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
          skipInventoryActions: true,
        });

        return errorModal$;
      }

      return of(PrintActions.printPackingSlip(selectedFulfillmentPlanIds, someFulfillmentsHavePrintedPackingSlip, true, autoPrint));
    }),
  );
};

export const downloadBulkLabel = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.DOWNLOAD_SHIPMENT_GRID_LABELS),
    mergeMap(() => {
      const state = getState();
      const fulfillmentIds = Global.selectSelectedShipmentsSelectedShipmentIds(state) || [];
      if (fulfillmentIds.length === 0) {
        return EMPTY;
      }
      const fulfillmentPlanIds = Global.selectEntitiesFPIdsByFulfillmentIds(state, { fulfillmentIds }) || emptyArray;
      const selectedFulfillmentPlans =
        Global.selectEntitiesFulfillmentPlansByIds(state, {
          fulfillmentPlanIds,
        }) || emptyArray;
      const someFulfillmentsHavePrintedLabel = selectedFulfillmentPlans.some((fulfillmentPlan) => fulfillmentPlan.isLabelPrinted);

      if (someFulfillmentsHavePrintedLabel) {
        const fulfillmentPlansWithWarnings = selectedFulfillmentPlans.filter((fulfillmentPlan) => fulfillmentPlan.isLabelPrinted);
        const mappedData = fulfillmentPlansWithWarnings.map(({ fulfillmentPlanId }) => ({
          id: fulfillmentPlanId,
          fulfillmentPlanId,
        }));
        const errorsAndWarnings = [BulkErrorsAndWarningsService.getLabelPrintedWarning(mappedData)];

        const onContinueAction = () => {
          return DocumentActions.downloadDocument(DocumentType.Label, fulfillmentIds);
        };
        const errorModal$ = bulkErrorsAndWarningsModalObservable({
          continueCanBeDisabled: true,
          dataType: 'fulfillmentPlanWithoutSalesOrder',
          errorsAndWarnings,
          fulfillmentPlanSetId: undefined,
          hasAdditionalAction: false,
          hasSelectedItems: !!selectedFulfillmentPlans.length,
          isBatchPrinting: false,
          onContinueAction,
          selectedItems: selectedFulfillmentPlans,
          title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
        });

        return errorModal$;
      }

      return of(DocumentActions.downloadDocument(DocumentType.Label, fulfillmentIds));
    }),
  );
};

export const directPrintBulkLabel = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.DIRECT_PRINT_SHIPMENT_GRID_LABELS),
    mergeMap(() => {
      const state = getState();
      const fulfillmentIds = Global.selectSelectedShipmentsSelectedShipmentIds(state) || [];
      if (fulfillmentIds.length === 0) {
        return EMPTY;
      }
      const fulfillmentPlanIds = Global.selectEntitiesFPIdsByFulfillmentIds(state, { fulfillmentIds }) || emptyArray;
      const selectedFulfillmentPlans =
        Global.selectEntitiesFulfillmentPlansByIds(state, {
          fulfillmentPlanIds,
        }) || emptyArray;
      const someFulfillmentPlansHavePrintedLabel = selectedFulfillmentPlans.some((fulfillmentPlan) => fulfillmentPlan.isLabelPrinted);

      if (someFulfillmentPlansHavePrintedLabel) {
        const fulfillmentPlansWithWarnings = selectedFulfillmentPlans.filter((fulfillmentPlan) => fulfillmentPlan.isLabelPrinted);
        const mappedData = fulfillmentPlansWithWarnings.map(({ fulfillmentPlanId }) => ({
          id: fulfillmentPlanId,
          fulfillmentPlanId,
        }));
        const errorsAndWarnings = [BulkErrorsAndWarningsService.getLabelPrintedWarning(mappedData)];

        const onContinueAction = () => {
          return DocumentActions.printDocument(
            DocumentType.Label,
            fulfillmentIds,
            DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.Label, false),
          );
        };
        const errorModal$ = bulkErrorsAndWarningsModalObservable({
          continueCanBeDisabled: true,
          dataType: 'fulfillmentPlanWithoutSalesOrder',
          errorsAndWarnings,
          fulfillmentPlanSetId: undefined,
          hasAdditionalAction: false,
          hasSelectedItems: !!selectedFulfillmentPlans.length,
          isBatchPrinting: false,
          onContinueAction,
          selectedItems: selectedFulfillmentPlans,
          title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
        });

        return errorModal$;
      }

      return of(
        DocumentActions.printDocument(
          DocumentType.Label,
          fulfillmentIds,
          DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.Label, false),
        ),
      );
    }),
  );
};

export const printBulkLabel = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_SHIPMENT_GRID_LABELS),
    mergeMap(({ autoPrint }) => {
      const state = getState();
      const fulfillmentIds = Global.selectSelectedShipmentsSelectedShipmentIds(state) || [];
      if (fulfillmentIds.length === 0) {
        return EMPTY;
      }
      const fulfillmentPlanIds = Global.selectEntitiesFPIdsByFulfillmentIds(state, { fulfillmentIds }) || emptyArray;
      const selectedFulfillmentPlans =
        Global.selectEntitiesFulfillmentPlansByIds(state, {
          fulfillmentPlanIds,
        }) || emptyArray;
      const someFulfillmentPlansHavePrintedLabel = selectedFulfillmentPlans.some((fulfillmentPlan) => fulfillmentPlan.isLabelPrinted);

      if (someFulfillmentPlansHavePrintedLabel) {
        const fulfillmentPlansWithWarnings = selectedFulfillmentPlans.filter((fulfillmentPlan) => fulfillmentPlan.isLabelPrinted);
        const mappedData = fulfillmentPlansWithWarnings.map(({ fulfillmentPlanId }) => ({
          id: fulfillmentPlanId,
          fulfillmentPlanId,
        }));
        const errorsAndWarnings = [BulkErrorsAndWarningsService.getLabelPrintedWarning(mappedData)];

        const onContinueAction = (updatedData) => {
          const shipmentsToContinueWith = updatedData.selectedItems;
          const fulfillmentPlanIdsToContinueWith = shipmentsToContinueWith.map((fulfillment) => fulfillment.fulfillmentPlanId);
          const fulfillmentIdsToContinueWith = shipmentsToContinueWith.map((fulfillment) => fulfillment.fulfillmentId);
          return PrintActions.printLabel(fulfillmentPlanIdsToContinueWith, fulfillmentIdsToContinueWith, false, true, autoPrint);
        };
        const errorModal$ = bulkErrorsAndWarningsModalObservable({
          continueCanBeDisabled: true,
          dataType: 'fulfillmentPlanWithoutSalesOrder',
          errorsAndWarnings,
          fulfillmentPlanSetId: undefined,
          hasAdditionalAction: false,
          hasSelectedItems: !!selectedFulfillmentPlans.length,
          isBatchPrinting: false,
          onContinueAction,
          selectedItems: selectedFulfillmentPlans,
          title: BulkErrorsAndWarningsService.getPrintWarningsTitle(),
        });

        return errorModal$;
      }

      return of(PrintActions.printLabel(fulfillmentPlanIds, fulfillmentIds, undefined, true));
    }),
  );
};

export const setCustomColumnConfigurationOnFilterUpdate = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(FilteringActions.SHIPMENTS_AUTOSWITCH_FILTERVIEW_CUSTOM),
    mergeMap((action) => {
      const state = getState();
      const currentViewId = getSelectedViewFromPathName(history.location.pathname)?.id || 'undefined';
      const previousFilterView = action.filterView;
      const previousfilterViewId = previousFilterView.filterViewId;
      const userViewConfigurations = Global.selectShipmentsGridUserGridConfigurations(state);
      const customViewColumnConfiguration =
        get(userViewConfigurations, `${currentViewId}.${previousfilterViewId}`) || get(userViewConfigurations, `${currentViewId}.default`);
      return of(Actions.updateCustomViewGridConfiguration(customViewColumnConfiguration));
    }),
  );
};

const handleNotifyMarketplaceAsyncMessage = ({ jobGuids }) => {
  return new Observable((observer) => {
    const onMessage = (message) => {
      observer.next(message);
    };
    const unbindFromEvent = PusherService.bindToEvent('bulk-action:marketplace-notify', onMessage);
    return () => unbindFromEvent();
  }).pipe(
    filter((pusherMessage) => {
      return jobGuids.includes(pusherMessage.messageGuid);
    }),
    bufferCount(jobGuids.length),
    take(1),
    mergeMap((pusherMessages) => {
      const errors = pusherMessages
        .map((fulfill) => {
          const errorMessage = get(fulfill, 'marketplaceNotification.errorMessage');
          return errorMessage;
        })
        .filter((message) => !!message);
      if (errors.length) {
        return of(GridHeaderActions.notifyMarketplaceFailure(errors), ShipmentsActions.silentRequestGetShipments());
      }
      return of(GridHeaderActions.notifyMarketplaceSuccess(), ShipmentsActions.silentRequestGetShipments());
    }),
  );
};

export const notifyMarketplace = (action$, { getState }) => {
  return action$.pipe(
    ofType(GridHeaderActions.SHIPMENTS_GRID_NOTIFY_MARKETPLACE),
    mergeMap((action) => {
      const { fulfillmentIds } = action;
      const notifyMarketplaceNotificationProps = {
        title: 'Processing...',
        content: 'Notifying Marketplace',
        notificationType: 'info',
        duration: 1000,
      };
      const featureFlags = new FeatureFlags(getState());
      NotificationService.showNotification({
        ...notifyMarketplaceNotificationProps,
      });
      if (featureFlags.enableAsyncMarketplaceNotifications) {
        return ShipmentDetailsApi.notifyMarketplaceAsync(fulfillmentIds).pipe(
          mergeMap(({ data = {} }) => {
            return handleNotifyMarketplaceAsyncMessage({
              jobGuids: data.jobGuids,
            });
          }),
        );
      }
      return ShipmentDetailsApi.notifyMarketplace(fulfillmentIds).pipe(
        mergeMap(({ data = [] }) => {
          const errors = data
            .map((fulfill) => {
              const errorMessage = get(fulfill, 'marketplaceNotification.errorMessage');
              return errorMessage;
            })
            .filter((message) => !!message);
          if (errors.length) {
            return of(GridHeaderActions.notifyMarketplaceFailure(errors), ShipmentsActions.silentRequestGetShipments());
          }
          return of(GridHeaderActions.notifyMarketplaceSuccess(), ShipmentsActions.silentRequestGetShipments());
        }),
        catchError((error) => {
          return of(GridHeaderActions.notifyMarketplaceFailure(error), ShipmentsActions.silentRequestGetShipments());
        }),
      );
    }),
  );
};

export const updateAmazonShipmentNotifications = (action$, { getState }) => {
  return action$.pipe(
    ofType(ShipmentsActions.GET_SHIPMENTS_SUCCESS),
    mergeMap(() => {
      const state = getState();
      const amazonFeatureFlag = get(state, 'featureFlags.enableAmazonShipNotifyUpdates', false);
      if (!amazonFeatureFlag) {
        return EMPTY;
      }
      const AMAZON_CODE = 'amazon';
      const salesOrders = Global.selectEntitiesSalesOrders(state);

      const filterCriteria = (salesOrder) => salesOrder.store?.marketplaceCode?.includes(AMAZON_CODE) && salesOrder.fulfillmentIds.length;

      const amazonOrders = Object.values(salesOrders)
        .filter(filterCriteria)
        .map(({ store, orderNumber, fulfillmentIds }) => ({
          storeGuid: store?.storeGuid,
          amazonOrderId: orderNumber,
          fulfillmentIds,
        }));

      const ordersSortedByStoreGuid = amazonOrders.reduce((acc, curr) => {
        if (!acc[curr.storeGuid]) {
          return {
            ...acc,
            [curr.storeGuid]: {
              storeGuid: curr.storeGuid,
              amazonOrderIds: [curr.amazonOrderId],
            },
          };
        }
        return {
          ...acc,
          [curr.storeGuid]: {
            ...acc[curr.storeGuid],
            amazonOrderIds: [...acc[curr.storeGuid].amazonOrderIds, curr.amazonOrderId],
          },
        };
      }, {});
      const payload = Object.values(ordersSortedByStoreGuid);

      if (!payload.length) {
        return EMPTY;
      }
      return from(ShipmentsApi.amazonOrderNotificationUpdate(payload)).pipe(
        mergeMap((orderUpdates) => {
          const fulfillmentUpdates = {};
          amazonOrders.forEach(({ amazonOrderId, fulfillmentIds }) => {
            const fulfillmentId = fulfillmentIds[0];
            const orderResponse = orderUpdates.find((x) => x.amazonOrderId === amazonOrderId);
            // if we have a record of the fulfillment but the amazon shipnotify service
            // doesn't return a response, the notification has been sent but amazon
            // hasnt responded yet.
            if (!orderResponse) {
              fulfillmentUpdates[fulfillmentId] = {
                status: amazonShipNotifyFeedStatusMap.sent,
                sent: true,
              };
            } else {
              const { status, message } = orderResponse;
              fulfillmentUpdates[fulfillmentId] = {
                status: amazonShipNotifyFeedStatusMap[status],
                errorMessage: status === 'error' ? message?.description : undefined,
              };
            }
          });

          return of(Actions.patchAmazonShipmentNotifications(fulfillmentUpdates));
        }),
        catchError((err) => {
          const { Errors } = err?.response?.data ?? [];
          const errorDisplay = Errors.length ? Errors[0].Message : <FormattedMessage {...messages.genericFallbackErrorMessage} />;
          NotificationService.showNotification({
            title: '',
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: errorDisplay,
            duration: 3000,
          });
          return EMPTY;
        }),
      );
    }),
  );
};
